import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as ContentJobService from 'services/agents/content/content-job';

export const useContentJobService = () => {
  const { token, email } = useSelector((store) => store.user_data);

  const fetchContentJobs = async (options = {}) => {
    const { error, payload } = await ContentJobService.read({ ...options, token });

    if (error) {
      toast.error(error);
      return { content_jobs: [], size: 0 };
    }

    const {
      data: content_jobs,
      meta: { size }
    } = payload;
    return { content_jobs, size };
  };

  const fetchContentJob = async (id, options = {}) => {
    const { error, payload: content_job } = await ContentJobService.readById(id, {
      ...options,
      token
    });

    if (error) {
      toast.error(error);
      return { content_job: null };
    }

    return { content_job };
  };

  const fetchContentJobsByBlog = async (blog_id, options = {}) => {
    const { error, payload } = await ContentJobService.readByBlog(blog_id, { ...options, token });

    if (error) {
      toast.error(error);
      return { content_jobs: [] };
    }

    return { content_jobs: payload?.data || payload || [] };
  };

  const fetchContentJobVersions = async (id, options = {}) => {
    const { error, payload } = await ContentJobService.readVersions(id, { ...options, token });

    if (error) {
      toast.error(error);
      return { versions: [] };
    }

    return { versions: payload?.data || payload || [] };
  };

  const searchContentJobs = async (keys, keyword, options = {}) => {
    const { error, payload } = await ContentJobService.search(keys, keyword, {
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to fetch content jobs at this time.');
      return { content_jobs: [], size: 0 };
    }

    const {
      data: content_jobs,
      meta: { size }
    } = payload;
    return { content_jobs, size };
  };

  const acceptContentJob = async (id, options = {}) => {
    const { error, payload } = await ContentJobService.accept(id, {
      ...options,
      token,
      data: {
        approved_by: email,
        ...(options.data || {})
      }
    });

    if (error) {
      toast.error(error || 'Unable to approve this job at this time.');
      return { success: false, send_error: null };
    }

    const send_error = payload?.send_error || null;
    if (send_error) {
      toast.error(send_error);
      return { success: true, send_error };
    }

    toast.success('Content job approved successfully');
    return { success: true, send_error: null };
  };

  const rejectContentJob = async (id, rejection_comment, options = {}) => {
    const { error } = await ContentJobService.reject(id, {
      ...options,
      token,
      data: {
        rejection_comment,
        rejected_by: email,
        ...(options.data || {})
      }
    });

    if (error) {
      toast.error(error || 'Unable to reject this job at this time.');
      return false;
    }

    toast.success('Rejected — a new version is being generated');
    return true;
  };

  const updateContentJobOutput = async (id, output, approve = true, options = {}) => {
    const { error, payload } = await ContentJobService.saveEdit(id, {
      ...options,
      token,
      data: {
        output,
        approve,
        approved_by: email,
        ...(options.data || {})
      }
    });

    if (error) {
      toast.error(error || 'Unable to save your edits at this time.');
      return { success: false, send_error: null };
    }

    const send_error = payload?.send_error || null;
    if (send_error) {
      toast.error(send_error);
      return { success: true, send_error };
    }

    toast.success(approve ? 'Edits saved and approved' : 'Edits saved');
    return { success: true, send_error: null };
  };

  const triggerContentAgentRun = async (agent_type, blog_id, options = {}) => {
    const { error } = await ContentJobService.triggerRun(agent_type, {
      ...options,
      token,
      data: {
        blog_id,
        ...(options.data || {})
      }
    });

    if (error) {
      toast.error(error || 'Unable to run this agent at this time.');
      return false;
    }

    toast.success('Agent run started');
    return true;
  };

  return {
    acceptContentJob,
    fetchContentJob,
    fetchContentJobVersions,
    fetchContentJobs,
    fetchContentJobsByBlog,
    rejectContentJob,
    searchContentJobs,
    triggerContentAgentRun,
    updateContentJobOutput
  };
};
